/**
 * SearchPage — ask a question about the estate, by time, place, subject and appearance.
 *
 * ─── TWO AXES: WHAT TO LOOK FOR, AND WHAT TO ANSWER ───
 * This page used to build a filter and always answer with the same thing: a page of detections.
 * That under-served what operators actually ask. "Which areas did the red-shirt person visit",
 * "when were they in the office", "was anyone in the server room", "where did they go, in order",
 * "did everyone wear a helmet" — each is the same kind of filter with a completely different
 * ANSWER, and making someone read two hundred detections to work out which rooms appear in them
 * is how a search feature goes unused.
 *
 * So `filters.question` picks the answer shape (and with it the aggregate), while the filter
 * controls narrow what is being asked about.
 *
 * ─── THE QUESTION COMES FROM THE TYPED SENTENCE ───
 * There is deliberately NO answer-shape picker in the filter bar. `QueryBar` parses a sentence
 * and writes the question along with the filters, so the operator asks in words rather than
 * translating their question into a control first. The parse is never hidden: the bar names the
 * question it read, the status line says what the answer will be, and every filter it set is a
 * chip that can be changed by hand.
 *
 * The consequence to know: with the bar empty the page answers with the plain list, and the other
 * five shapes are reached by typing. Two controls therefore appear only when the parsed question
 * gives them meaning — the place grouping under Scope for "which areas", and the thing being
 * measured under Looking-for for "did all of them…".
 *
 * That split is also what makes the eventual neural search a one-function swap rather than a
 * second UI: `parseQuery` returns a parse, not results, and everything downstream is unchanged.
 *
 * ─── PERSON IS NOT PERSON + FACE ───
 * One human walking past a camera running both detectors produces a person event AND a face
 * event. The subject used to search both, invisibly, so every count read high against the Person
 * Detection report and looked like a bug. "Detected by" makes the choice visible and starts on
 * person detection alone; face is added when identity matters. The narrower default comes from
 * the API (`subjects.person.defaultModels`), so an older node keeps its own behaviour.
 *
 * ─── CLOSED LISTS, WITH ONE EXCEPTION ───
 * Attribute values are always picked from a vocabulary the server published, never typed. A text
 * box invites "crimson", which matches nothing, and an empty result reads as "nobody wore red" —
 * a confident wrong answer. The one free-text field is the vehicle PLATE, which is an identity
 * rather than a category; it is canonicalised on both sides and prefix-matched.
 *
 * ─── EVERY VALUE STAYS OFFERABLE ───
 * The controls show the FULL vocabulary and use facets only for the counts beside each value. A
 * count of 0 dims but never hides: setting up "find a blue shirt" before any blue has been seen
 * is a fair question, and a palette showing two of thirteen colours reads as broken.
 *
 * ─── RBAC ───
 * The page never sends a camera allow-list. Each node's backend resolves the requester's
 * accessible cameras and applies them server-side, overwriting anything a client sends. The
 * camera and group controls can only NARROW that set; `camera_scope` in the response reports what
 * the query actually ran against, which is what lets "no results" be told from "no access" — and
 * what stops the Yes/No answer from reporting a No about a place the user cannot see.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import { useApi } from '../api/ApiProvider';
import type {
  CameraRef,
  Group,
  SearchEventRow,
  SearchFacets,
  SearchJourneyRow,
  SearchPlaceBy,
  SearchResult,
  SearchSchema,
} from '../api/contracts';
import EventListCards from '../components/events/EventListCards';
import type { EventColumnLike } from '../components/events/EventDetail';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import RecordPager from '../components/common/RecordPager';
import {
  ActiveChip,
  FilterDropdown,
  OptionList,
  Swatch,
  SwatchGrid,
  type FilterOption,
} from '../components/search/FilterControls';
import SearchPlaybackPanel from '../components/search/SearchPlaybackPanel';
import EventTimelineStrip, { type TimeRange } from '../components/search/EventTimelineStrip';
import QueryBar from '../components/search/QueryBar';
import { parseQuery, type ParsedQuery } from '../components/search/queryParser';
import {
  COLOR_FIELDS,
  EMPTY_FILTERS,
  FIELD_LABEL,
  MODEL_GROUP,
  MODEL_LABEL,
  QUESTION_BY_KEY,
  SUBJECT_FALLBACK,
  SUBJECT_MODELS,
  TIME_PRESETS,
  activeCount,
  hasPlace as filtersHavePlace,
  modelsFor,
  resolveSubjectDef,
  summariseValues,
  titleCase,
  toDsl,
  windowScope,
  markerModelsFor,
  type Filters,
  type QuestionKey,
  type SubjectKey,
} from '../components/search/searchModel';
import WhenAnswer from '../components/search/answers/WhenAnswer';
import WhereAnswer from '../components/search/answers/WhereAnswer';
import VerdictAnswer from '../components/search/answers/VerdictAnswer';
import JourneyAnswer from '../components/search/answers/JourneyAnswer';
import ComplianceAnswer from '../components/search/answers/ComplianceAnswer';
import { makePlaceNamer } from '../components/search/answers/common';

/**
 * Shown inside an appearance panel when the current scope has recorded none of that attribute.
 *
 * A notice rather than a disabled control: the values stay pickable, because "nothing in this
 * window" is not the same as "this can never match". The distinction that matters is stated
 * plainly, because the two causes have completely different fixes — widen the window, or turn
 * attribute extraction on for those cameras.
 */
const NOT_COLLECTED_NOTICE =
  'Nothing recorded for this in the current scope — either no such events yet, or attribute '
  + 'extraction is not enabled on these cameras. You can still select values to search.';

/**
 * The card facts, as a synthetic column set.
 *
 * EventListCards derives a card's facts from a model's column definitions. Search rows are
 * cross-model and uniform, so instead of the per-model definitions the reports use, this is one
 * set describing the projection — which is what keeps a person event and a fire event rendering
 * as the same kind of card in one result grid.
 */
const RESULT_COLUMNS: EventColumnLike[] = [
  { key: 'camera_id', label: 'Camera', type: 'camera' },
  { key: 'model_key', label: 'Model', render: (v: string) => MODEL_LABEL[v] || titleCase(v || '') },
  {
    key: 'subject_label',
    label: 'Identity',
    // Subject-neutral since migration 050: the same column carries a person's name and a
    // vehicle's plate, so one card renderer serves both subjects.
    render: (v: string, row: any) => {
      if (v) return v;
      if (row?.subject_kind === 'vehicle') return 'No plate read';
      return row?.is_known ? 'Known' : 'Unknown';
    },
  },
  {
    key: 'attributes',
    label: 'Appearance',
    render: (v: Record<string, any>) => {
      if (!v || typeof v !== 'object') return '—';
      const parts: string[] = [];
      if (v.upper_color) parts.push(`${v.upper_color} top`);
      if (v.lower_color) parts.push(`${v.lower_color} bottom`);
      if (v.gender && v.gender !== 'unknown') parts.push(v.gender);
      if (Array.isArray(v.accessories) && v.accessories.length) parts.push(v.accessories.join(', '));
      return parts.length ? parts.join(' · ') : '—';
    },
  },
];

/** How the place buckets are labelled in the "Which areas?" control. */
const PLACE_BY_OPTIONS: Array<{ value: SearchPlaceBy; label: string; hint: string }> = [
  { value: 'group', label: 'Area (camera group)', hint: 'Named, nested areas — how people describe a site.' },
  { value: 'camera', label: 'Camera', hint: 'One row per camera. No nesting, so the rows add up.' },
  { value: 'zone', label: 'Zone within a view', hint: 'Regions of interest, where the cameras have them configured.' },
];

// ─── Props ─────────────────────────────────────────────────────────────────

export interface SearchPageProps {
  /** Changes when the operator switches node (cloud); resets results and refetches lookups. */
  nodeKey?: string;
  /** A result's frame as a directly-loadable URL (local). Return null to fall back to loadImage. */
  imageFor?: (row: SearchEventRow) => string | null;
  /** Lazy frame resolver, for hosts that must stream images through a tunnel (cloud). */
  loadImage?: (row: SearchEventRow) => Promise<string | null>;
  /**
   * Open one result in the host's routed event-detail page.
   *
   * With `eventDetail` supplied this is no longer what a card click does — it becomes the
   * modal's "Full page" escape hatch, for the times the record has to outlive the search that
   * found it (a link in a ticket, a bookmark, a second monitor).
   */
  onOpenEvent?: (row: SearchEventRow) => void;
  /**
   * Open a result in a MODAL over the results, rather than navigating away.
   *
   * A search is a position an operator is working through — the same relationship a report table
   * has to its rows, which is why those open a modal too. Navigating to a page throws the query,
   * the answer and the scroll position away to look at one hit, and coming back means rebuilding
   * all three. So a card opens a modal, and ← / → step to the next hit without closing it.
   *
   * Grouped into one prop because the three parts are useless apart, and split this way because
   * of where the knowledge lives:
   *   `load`   — a search row comes from the PROJECTION and carries only what the index needs
   *              (id, camera, timestamp, attributes, frames). The detail body wants the
   *              typed-table record, so it has to be fetched, and only the host knows how.
   *   `render` — `EventDetailBody` is host-local (each app loads snapshots differently), so the
   *              body cannot be built here. Handed the page's own camera-name map so the record
   *              names cameras exactly as the cards above it do, and without a second fetch.
   *   `title`  — likewise.
   *
   * Omit it and a card click falls back to `onOpenEvent`, which is what this page did before.
   */
  eventDetail?: {
    load: (row: SearchEventRow) => Promise<any>;
    render: (
      event: any,
      row: SearchEventRow,
      ctx: { cameraNames: Record<string, string> },
    ) => ReactNode;
    title?: (event: any, row: SearchEventRow) => ReactNode;
  };
  /**
   * Offer the per-result Playback control. Default on.
   *
   * The player itself is shared now (`RecordingPlaybackPlayer`), so no host has to supply one —
   * this exists only for a host that should not offer recording playback at all.
   */
  playback?: boolean;
  /** Longer HLS timeouts, for a stream crossing the cloud's WebSocket tunnel. */
  playbackTunnelled?: boolean;
  /** Jump to the full Recordings page at this camera and moment. */
  onOpenRecordings?: (row: SearchEventRow, fromIso: string, toIso: string) => void;
  /** Rendered instead of the page when the host has nothing to query (cloud: no node selected). */
  emptyState?: ReactNode;
}

// ─── Page ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 24;

/**
 * The row budget per question, because the aggregates page different things.
 *
 * `journey` pages SUBJECTS, not rows, and each one expands into a trail — twenty is already a
 * long scroll. `attribute_split` uses the limit for its named "without" list, where fifty is
 * enough to be a roster rather than a sample. The place and timeline aggregates are bounded by
 * the estate and the window, so their limit is never reached.
 */
function limitFor(question: QuestionKey): number {
  if (question === 'journey') return 20;
  if (question === 'compliance') return 50;
  if (question === 'list' || question === 'verdict') return PAGE_SIZE;
  return PAGE_SIZE;
}

/**
 * The footage window around an event: a little before, more after.
 *
 * Asymmetric on purpose. What an operator wants to see is what HAPPENED — the approach matters
 * less than the aftermath — but starting exactly at the detection gives no run-up at all, and a
 * person is usually already mid-frame by the time a model fires. Ten seconds of lead is enough
 * to see them arrive; forty-five after covers the incident itself.
 *
 * The transport's ±10s / ±30s jumps cover anything outside that, and the panel offers a link to
 * the full Recordings timeline for real review.
 */
const PLAYBACK_LEAD_SEC = 10;
const PLAYBACK_TAIL_SEC = 45;

export default function SearchPage({
  nodeKey = '',
  imageFor,
  loadImage,
  onOpenEvent,
  eventDetail,
  playback = true,
  playbackTunnelled = false,
  onOpenRecordings,
  emptyState,
}: SearchPageProps) {
  const { search } = useApi();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);

  const [schema, setSchema] = useState<SearchSchema | null>(null);
  const [facets, setFacets] = useState<SearchFacets>({});
  const [cameras, setCameras] = useState<CameraRef[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  /**
   * The last text parse, or null.
   *
   * Cleared the moment a control is touched by hand, because the "Read as" row is a claim about
   * what the current query IS. A stale claim beside filters that have since changed is worse than
   * no claim at all.
   */
  const [parsed, setParsed] = useState<ParsedQuery | null>(null);

  /** The row whose footage is open in the docked panel, or null. */
  const [playbackRow, setPlaybackRow] = useState<SearchEventRow | null>(null);

  /**
   * The open record, as an INDEX into the rows on screen rather than the row itself.
   *
   * An index is what lets ← / → step to the next hit: the pager moves the index and the modal
   * follows. Holding the row instead would make "next" impossible without also holding where it
   * came from.
   */
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [detailEvent, setDetailEvent] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  /**
   * Event rows for the Yes/No answer's evidence, fetched separately.
   *
   * The verdict runs on `by_place`, so `result.rows` are PLACES — they carry no event id, camera
   * or timestamp, and handing them to the card grid would render a row of broken cards. The two
   * cannot come from one call: the place aggregate is what gives the exact first and last sighting
   * across the whole match set, while a `list` page would only ever know about its own page. So a
   * Yes gets one extra request for the frames, which is the one question where seeing them is the
   * point.
   */
  const [evidenceRows, setEvidenceRows] = useState<SearchEventRow[]>([]);

  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The bottom strip's own answer — the `timeline` aggregate over the whole window.
   *
   * A second query rather than a second reading of `result`, and it has to be: the answer is
   * whatever shape the question asked for (cards, places, trails), and once a range is selected
   * the answer covers only that range. The strip has to keep drawing the window AROUND the
   * selection, so it asks for the window on every filter except the selection itself.
   */
  const [timeline, setTimeline] = useState<SearchResult | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  /** The high-severity rows the timeline pins, or empty when the query searches none. */
  const [markers, setMarkers] = useState<SearchEventRow[]>([]);

  /** Narrow a filter dimension and return to page 1 — a filter change invalidates the offset. */
  const set = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // A range is a slice of a WINDOW. Moving the window leaves the slice describing a time the
      // query no longer covers, so it goes with it rather than silently emptying the results.
      ...(key === 'time' ? { range: null } : null),
    }));
  }, []);

  /** The strip's selection. Its own setter, because a range never changes the page's own filters. */
  const setRange = useCallback((range: TimeRange | null) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => ({ ...prev, range }));
  }, []);

  const setAttribute = useCallback((field: string, values: string[]) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => ({ ...prev, attributes: { ...prev.attributes, [field]: values } }));
  }, []);

  const cameraNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cameras) m.set(c.id, c.name);
    return m;
  }, [cameras]);

  const groupNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups as any[]) m.set(g.id, g.name);
    return m;
  }, [groups]);

  const namePlace = useMemo(() => makePlaceNamer(cameras, groups), [cameras, groups]);

  /**
   * The same names, as the plain object both hosts' `EventDetailBody` takes.
   *
   * Derived from the Map rather than fetched again: the detail modal must name a camera the same
   * way the card behind it does, and two lookups of the same list are two chances to disagree.
   */
  const cameraNameMap = useMemo(
    () => Object.fromEntries(cameraNames) as Record<string, string>,
    [cameraNames],
  );

  // ── Lookups. Re-fetched per node; failures are non-fatal so a missing camera list degrades
  //    the controls to ids rather than breaking search itself.
  useEffect(() => {
    let live = true;
    setSchema(null); setCameras([]); setGroups([]); setResult(null);
    setFilters(EMPTY_FILTERS); setPage(0); setParsed(null);

    (async () => {
      const [s, c, g] = await Promise.allSettled([search.schema(), search.listCameras(), search.listGroups()]);
      if (!live) return;
      if (s.status === 'fulfilled') setSchema(s.value);
      if (c.status === 'fulfilled') setCameras(c.value || []);
      if (g.status === 'fulfilled') setGroups(g.value || []);
    })();

    return () => { live = false; };
  }, [search, nodeKey]);

  const subjects = schema?.subjects;
  const subjectDef = useMemo(
    () => resolveSubjectDef(subjects, filters.subject),
    [subjects, filters.subject],
  );

  /** The subject tabs, in the API's order where it gives one. */
  const subjectKeys = useMemo<SubjectKey[]>(() => {
    const keys = Object.keys(subjects || SUBJECT_FALLBACK) as SubjectKey[];
    return keys.filter((k) => SUBJECT_FALLBACK[k] || subjects?.[k]);
  }, [subjects]);

  const questionSpec = QUESTION_BY_KEY[filters.question] || QUESTION_BY_KEY.list;
  const limit = limitFor(filters.question);

  const dsl = useMemo(
    () => toDsl(filters, subjectDef, limit, page * limit),
    [filters, subjectDef, limit, page],
  );

  // ── Facets follow the SCOPE only (time/camera/group/model), never the appearance selection:
  //    they describe what is available to pick, so narrowing by one colour must not hide the rest.
  //    The dragged RANGE is part of that scope, and has to be in this key: `toDsl` already sends
  //    it, so leaving it out left the counts describing the whole window while the query ran
  //    against one hour of it.
  const scopeKey = useMemo(() => JSON.stringify({
    t: filters.time, r: filters.range, g: filters.groups, c: filters.cameras,
    s: filters.subject, sm: filters.subjectModels, m: filters.otherModels,
  }), [filters.time, filters.range, filters.groups, filters.cameras, filters.subject,
    filters.subjectModels, filters.otherModels]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Forced to the plain list: the facet endpoint takes the same DSL, and a `place_by` or
        // `split_by` meant for another aggregate would be rejected outright.
        const scopeOnly: Filters = {
          ...filters, question: 'list', attributes: {}, known: null, plate: '', splitBy: null,
        };
        const f = await search.facets(toDsl(scopeOnly, subjectDef, 1, 0));
        if (live) setFacets(f || {});
      } catch {
        // A failed facet load must not block searching — the controls fall back to the full
        // vocabulary, and an unmatched value still returns a clear message from the server.
        if (live) setFacets({});
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, nodeKey, scopeKey]);

  /**
   * The strip's DSL: the same query, asked as "when?", with the selection dropped.
   *
   * Keyed on itself rather than on the filter object, so selecting a range — the one change that
   * must NOT redraw the bars — does not refetch them. Everything else does: pick a colour and
   * the day visibly reshapes.
   */
  const timelineDsl = useMemo(
    () => toDsl(windowScope(filters), subjectDef, 1, 0),
    [filters, subjectDef],
  );
  const timelineKey = useMemo(() => JSON.stringify(timelineDsl), [timelineDsl]);

  useEffect(() => {
    let live = true;
    setTimelineLoading(true);
    (async () => {
      try {
        const r = await search.events(timelineDsl);
        if (live) setTimeline(r);
      } catch {
        // The strip is a control over the answer, not the answer. A failed timeline leaves the
        // search entirely usable, so it says so in its own space rather than raising the page's
        // error banner over a working result.
        if (live) setTimeline(null);
      } finally {
        if (live) setTimelineLoading(false);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, nodeKey, timelineKey]);

  /**
   * The timeline's marker pins — the rare, high-severity rows, fetched as rows.
   *
   * Bounded twice over: only when the query actually searches one of those models (so a person
   * search issues no request at all and shows no rail), and capped at 60 rows, which is
   * affordable exactly because the events that earn a pin are rare. Anything more would be a
   * bar chart's job.
   */
  const markerModels = useMemo(
    () => markerModelsFor(filters, subjectDef),
    [filters, subjectDef],
  );
  const markerKey = useMemo(
    () => (markerModels.length ? JSON.stringify({ m: markerModels, t: timelineKey }) : ''),
    [markerModels, timelineKey],
  );

  useEffect(() => {
    if (!markerKey) { setMarkers([]); return undefined; }
    let live = true;
    (async () => {
      try {
        /*
         * Time and place only. Two deliberate departures from the answer's own DSL:
         *
         * `models` is OVERWRITTEN rather than passed through `otherModels`, because `toDsl`
         * always folds in the subject's own models — which would drag `person` into a query
         * that is meant to be about fires alone.
         *
         * `attributes` are dropped, because none of these models carries one. A fire has no
         * shirt colour, so sending "upper_color: red" would not narrow the rail, it would empty
         * it — and an empty rail beside a red-shirt search would read as "no fires today".
         */
        const dslForMarkers = toDsl(
          { ...windowScope(filters), question: 'list', attributes: {}, known: null, plate: '' },
          subjectDef,
          60,
          0,
        );
        dslForMarkers.models = markerModels;
        const r = await search.events(dslForMarkers);
        if (live) setMarkers((r.rows || []) as SearchEventRow[]);
      } catch {
        // The rail is an annotation on a control. Losing it must not disturb the answer, so it
        // simply does not draw.
        if (live) setMarkers([]);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, nodeKey, markerKey]);

  /**
   * The event rows currently on screen — what the modal's ← / → step through.
   *
   * Derived rather than captured when a card is clicked, because the two answers that show cards
   * get them from different places: the plain list reads `result.rows`, while the Yes/No answer's
   * evidence comes from its own separate fetch. Deriving keeps "next" pointing at the same set
   * the operator is looking at.
   */
  const detailRows = useMemo<SearchEventRow[]>(() => (
    filters.question === 'verdict' ? evidenceRows : ((result?.rows || []) as SearchEventRow[])
  ), [filters.question, evidenceRows, result]);

  const detailRow = detailIndex !== null ? (detailRows[detailIndex] ?? null) : null;

  /**
   * Fetch the full record behind the open card.
   *
   * A search row is a PROJECTION row: enough to rank, filter and draw a card, and not enough for
   * a detail view — it has no model-specific columns, no match scores, no zone geometry. The host
   * resolves the typed-table record, keyed on (model, event_id), which is exactly what the
   * projection stores and what the routed event page already looks up.
   */
  useEffect(() => {
    if (!eventDetail || !detailRow) {
      setDetailEvent(null);
      setDetailError(null);
      return undefined;
    }
    let live = true;
    setDetailLoading(true);
    setDetailError(null);
    (async () => {
      try {
        const ev = await eventDetail.load(detailRow);
        if (!live) return;
        setDetailEvent(ev || null);
        // A row the index has but the source table does not: past retention, or deleted. Said
        // plainly, because an empty modal reads as a broken page rather than a gone record.
        if (!ev) setDetailError('That event is no longer available — it may have been deleted or passed its retention window.');
      } catch {
        if (live) {
          setDetailEvent(null);
          setDetailError('Could not load that event.');
        }
      } finally {
        if (live) setDetailLoading(false);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDetail, detailRow?.model_key, detailRow?.event_id]);

  /**
   * The playback window for a row, as ISO strings.
   *
   * Returns null when there is no usable timestamp — a row that cannot say WHEN it happened
   * cannot be played, and offering the control would be offering a dead button.
   */
  const playbackWindow = useCallback((row: SearchEventRow | null) => {
    if (!row?.event_timestamp || !row?.camera_id) return null;
    const at = new Date(row.event_timestamp).getTime();
    if (!Number.isFinite(at)) return null;
    return {
      from: new Date(at - PLAYBACK_LEAD_SEC * 1000).toISOString(),
      to: new Date(at + PLAYBACK_TAIL_SEC * 1000).toISOString(),
    };
  }, []);

  /**
   * The question can be un-answerable with the filters as they stand — a Yes/No with nowhere to
   * check, a compliance ratio with nothing to measure. The panel says what is missing; running
   * the query anyway would spend a round trip to produce something that cannot be shown.
   */
  const answerable = !(
    (questionSpec.needsPlace && !filtersHavePlace(filters))
    || (questionSpec.needsSplit && !filters.splitBy)
  );

  const runSearch = useCallback(async () => {
    // A new query invalidates the open record's POSITION — index 3 of the old answer is a
    // different event in the new one — so the modal closes rather than silently re-pointing.
    setDetailIndex(null);
    if (!answerable) { setResult(null); setError(null); return; }
    setLoading(true);
    setError(null);
    try {
      setResult(await search.events(dsl));
    } catch (err: any) {
      // The server's message names the offending field ("crimson is not collected for
      // upper_color"). Surfacing it verbatim is the whole point of validating strictly.
      setError(err?.message || 'Search failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [search, dsl, answerable]);

  useEffect(() => { runSearch(); /* the filters ARE the query */ }, [runSearch]);

  /** The Yes/No answer's frames — see `evidenceRows`. Failure is silent: the verdict still holds. */
  useEffect(() => {
    if (filters.question !== 'verdict' || !answerable) { setEvidenceRows([]); return undefined; }
    let live = true;
    (async () => {
      try {
        const r = await search.events(toDsl({ ...filters, question: 'list' }, subjectDef, 12, 0));
        if (live) setEvidenceRows((r.rows || []) as SearchEventRow[]);
      } catch {
        // The verdict came from its own query and is unaffected. Showing it without the frames is
        // a worse answer than showing it with them, and a far better one than showing neither.
        if (live) setEvidenceRows([]);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dsl, answerable, filters.question]);

  /**
   * Apply a typed sentence to the controls.
   *
   * `EMPTY_FILTERS` as the base, not the current filters: a new sentence is a new question, and
   * silently intersecting it with whatever was left selected from the last one would answer
   * something the operator did not type. The subject is the exception — it is kept unless the
   * sentence names another, because that is the tab they are working in.
   */
  const applyParse = useCallback((text: string) => {
    const p = parseQuery(text, { schema, cameras, groups, subject: filters.subject });
    setPage(0);
    setFilters({ ...EMPTY_FILTERS, subject: filters.subject, ...p.filters });
    setParsed(p);
    return p;
  }, [schema, cameras, groups, filters.subject]);

  if (emptyState) return <>{emptyState}</>;

  /**
   * The result's rows, deliberately UNTYPED here.
   *
   * What they are depends on the aggregate — event rows for the list, places for "which areas"
   * and the verdict, trails for a journey, subjects for a compliance split. Each answer narrows
   * them itself; a blanket `as SearchEventRow[]` at this level would be a claim that is false for
   * four of the six questions, and it is what let place rows reach the card grid once already.
   * Only `.length` is read here, for the pager.
   */
  const rows = (result?.rows || []) as unknown[];
  const total = result?.events ?? 0;
  const distinct = result?.distinct_persons;
  const nActive = activeCount(filters, subjectDef);
  const attributeFields = subjectDef.fields;
  const activeModels = modelsFor(filters, subjectDef);

  /**
   * The attribute filter that had to be set aside for a compliance question.
   *
   * Filtering to helmets and then measuring helmets is always 100%; the server rejects it, and
   * `toDsl` drops the filter so the rest of the query still runs. The one thing not allowed is
   * doing that silently, so the name comes back here for the panel to report.
   */
  const setAsideFilter = filters.question === 'compliance' && filters.splitBy
    && (filters.attributes[filters.splitBy.field] || []).includes(filters.splitBy.value)
    ? (FIELD_LABEL[filters.splitBy.field] || titleCase(filters.splitBy.field))
    : null;

  // ── Summaries for the closed dropdown buttons ──
  const timeSummary = filters.time.from && filters.time.to
    ? `${filters.time.from} → ${filters.time.to}`
    : (TIME_PRESETS.find((p) => p.value === filters.time.preset)?.label || 'Today');
  /**
   * The dragged range, in words. Shows the DATE too once the window spans more than a day —
   * "09:00 – 10:00" is ambiguous across a thirty-day window, and the chip is the only place the
   * narrowing is visible without looking at the chart.
   */
  const rangeSummary = (() => {
    if (!filters.range) return '';
    const from = new Date(filters.range.from);
    const to = new Date(filters.range.to);
    const sameDay = from.toDateString() === to.toDateString();
    const t = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    const d = (x: Date) => x.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return sameDay ? `${d(from)} ${t(from)} – ${t(to)}` : `${d(from)} ${t(from)} – ${d(to)} ${t(to)}`;
  })();

  const listSummary = (ids: string[], names: Map<string, string>, all = 'All') =>
    ids.length === 0 ? all
      : ids.length === 1 ? (names.get(ids[0]) || ids[0].slice(0, 8))
        : `${ids.length} selected`;

  /** The chosen place in words, for the Yes/No headline. */
  const placeLabel = (() => {
    const names = [
      ...filters.groups.map((id) => groupNames.get(id) || id.slice(0, 8)),
      ...filters.cameras.map((id) => cameraNames.get(id) || id.slice(0, 8)),
    ];
    if (names.length === 0) return 'anywhere';
    if (names.length <= 2) return names.join(' or ');
    return `${names.length} places`;
  })();

  /**
   * What is being searched for, as a phrase — "a person in a red top carrying a helmet".
   *
   * Built from the filters rather than the typed sentence, so it describes the query that ran
   * even when the operator adjusted the chips afterwards.
   */
  const subjectPhrase = (() => {
    const bits: string[] = [];
    const attr = (field: string) => filters.attributes[field] || [];
    if (attr('upper_color').length) bits.push(`a ${attr('upper_color').join(' or ')} top`);
    if (attr('lower_color').length) bits.push(`${attr('lower_color').join(' or ')} bottoms`);
    if (attr('gender').length) bits.push(attr('gender').join(' or '));
    if (attr('accessories').length) bits.push(`carrying a ${attr('accessories').join(' or ')}`);
    if (attr('vehicle_type').length) bits.push(attr('vehicle_type').join(' or '));
    if (filters.plate.trim()) bits.push(`plate ${filters.plate.trim().toUpperCase()}`);
    const noun = filters.subject === 'vehicle' ? 'a vehicle' : filters.known ? 'an enrolled person' : 'a person';
    return bits.length ? `${noun} with ${bits.join(', ')}` : noun;
  })();

  /** Every attribute value, per field, for the "what to measure" control. */
  const splitOptions: FilterOption[] = attributeFields.flatMap((field) => (
    (schema?.attributes?.[field] || [])
      .filter((v) => v !== 'unknown')
      .map<FilterOption>((v) => ({
        value: `${field}:${v}`,
        label: titleCase(v),
        group: FIELD_LABEL[field] || titleCase(field),
        count: (facets[field] || []).find((f) => f.value === v)?.events ?? 0,
      }))
  ));

  /**
   * The card grid, over the rows it is GIVEN.
   *
   * Takes its rows as an argument rather than closing over `result.rows`, because what those rows
   * are depends on the aggregate: event rows for the plain list, but places for the verdict,
   * trails for a journey and subjects for a compliance split. Passing them in is what makes it
   * impossible to render a place row as an event card.
   */
  const cardGrid = (eventRows: SearchEventRow[]) => (
    <EventListCards
      events={eventRows}
      columns={RESULT_COLUMNS}
      loading={loading}
      context={{ cameraName: (id) => cameraNames.get(id) }}
      imageFor={imageFor ? (e) => imageFor(e as SearchEventRow) : undefined}
      loadImage={loadImage ? (e) => loadImage(e as SearchEventRow) : undefined}
      // The modal wins when the host supplied one. `onOpenEvent` is not dropped — it becomes the
      // modal's "Full page" button, so the routed record stays reachable for links and bookmarks.
      onOpen={(index) => {
        if (eventDetail) setDetailIndex(index);
        else onOpenEvent?.(eventRows[index]);
      }}
      cardAction={playback ? (event: any) => {
        // Offered whenever the row has a usable timestamp. A camera with no recording
        // still gets the button — the panel then says WHY (retention, transcode, recording
        // off), which is more useful than a control that silently is not there.
        if (!playbackWindow(event as SearchEventRow)) return null;
        return (
          <button
            type="button"
            onClick={() => setPlaybackRow(event as SearchEventRow)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground hover:bg-muted"
            title="Play the recording around this moment"
          >
            <PlayCircleIcon className="h-3.5 w-3.5" />
            Playback
          </button>
        );
      } : undefined}
      emptyTitle={result?.camera_scope === 0 ? 'No cameras in scope' : 'Nothing matched'}
      emptyHint={
        result?.camera_scope === 0
          ? 'You do not have access to any camera matching these filters.'
          : 'Try widening the time window, or removing an appearance filter.'
      }
    />
  );

  /** Narrow the query to one place — how a "which areas" row becomes the next search. */
  const pickPlace = (place: string, placeBy: SearchPlaceBy) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => (placeBy === 'group'
      ? { ...prev, groups: [...new Set([...prev.groups, place])], question: 'list' }
      : { ...prev, cameras: [...new Set([...prev.cameras, place])], question: 'list' }));
  };

  function renderAnswer() {
    if (!answerable || !result) {
      // The two un-answerable states own their own explanation, because what is missing differs.
      if (filters.question === 'verdict') {
        return (
          <VerdictAnswer
            result={result || { success: true }}
            namePlace={namePlace}
            placeLabel={placeLabel}
            hasPlace={filtersHavePlace(filters)}
            subjectPhrase={subjectPhrase}
          />
        );
      }
      if (filters.question === 'compliance') {
        return (
          <ComplianceAnswer
            result={result || { success: true }}
            knownOnly={filters.known === true}
            setAsideFilter={setAsideFilter}
          />
        );
      }
      return null;
    }

    switch (filters.question) {
      case 'when':
        return <WhenAnswer result={result} />;
      case 'where':
        return <WhereAnswer result={result} namePlace={namePlace} groups={groups} onPickPlace={pickPlace} />;
      case 'verdict':
        return (
          <VerdictAnswer
            result={result}
            namePlace={namePlace}
            placeLabel={placeLabel}
            hasPlace
            subjectPhrase={subjectPhrase}
            evidence={evidenceRows.length > 0 ? (
              <div className="min-h-0">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  The evidence
                </div>
                {cardGrid(evidenceRows)}
              </div>
            ) : undefined}
          />
        );
      case 'journey':
        return (
          <JourneyAnswer
            result={result}
            namePlace={namePlace}
            onPickSubject={(row: SearchJourneyRow) => {
              if (!row.subject_label) return;
              setPage(0);
              setParsed(null);
              setFilters((prev) => ({ ...prev, known: true, attributes: {} }));
            }}
          />
        );
      case 'compliance':
        return (
          <ComplianceAnswer
            result={result}
            knownOnly={filters.known === true}
            setAsideFilter={setAsideFilter}
          />
        );
      case 'list':
      default:
        return cardGrid(rows as SearchEventRow[]);
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* ── Header ── */}
      <div className="flex items-center gap-2 border-b border-border bg-card px-4 py-2.5">
        <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold text-foreground">Search</h1>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          Ask about the estate — who, where, when, and whether
        </span>
        <button
          type="button"
          onClick={runSearch}
          className="ml-auto flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-muted"
          title="Run the search again"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* ── The text bar. Sets the controls below; never queries on its own. ── */}
      <QueryBar
        onSubmit={applyParse}
        parsed={parsed}
        onClear={() => { setParsed(null); setPage(0); setFilters({ ...EMPTY_FILTERS, subject: filters.subject }); }}
      />

      {/* ── Filter bar. Three rows: where to look, what to look for, and what is selected. ── */}
      <div className="border-b border-border bg-card px-4 py-2.5">
        {/* Row 1 — SCOPE: where and when to look */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-[4.5rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Scope
          </span>

          <FilterDropdown label="Time" summary={timeSummary} active={filters.time.preset !== 'today' || !!filters.time.from}>
            {(close) => (
              <div>
                <OptionList
                  mode="single"
                  options={TIME_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
                  selected={filters.time.from ? [] : [filters.time.preset || 'today']}
                  onChange={([v]) => set('time', { preset: v })}
                  close={close}
                />
                {/* Custom range last: presets answer almost every question, and an operator who
                    needs an exact window knows to look past them. */}
                <div className="mt-1 border-t border-border pt-1.5">
                  <div className="px-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Custom range
                  </div>
                  <div className="flex flex-col gap-1 px-1.5 pb-1">
                    <input
                      type="datetime-local"
                      value={filters.time.from || ''}
                      onChange={(e) => set('time', { ...filters.time, preset: undefined, from: e.target.value })}
                      className="rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground"
                    />
                    <input
                      type="datetime-local"
                      value={filters.time.to || ''}
                      onChange={(e) => set('time', { ...filters.time, preset: undefined, to: e.target.value })}
                      className="rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground"
                    />
                    {filters.time.from && !filters.time.to && (
                      <p className="text-[10px] text-amber-500">Set both ends to use a custom range.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </FilterDropdown>

          <FilterDropdown
            label="Groups"
            summary={listSummary(filters.groups, groupNames)}
            active={filters.groups.length > 0}
            disabled={groups.length === 0}
            disabledReason="No camera groups on this node"
            panelClassName="w-72"
          >
            {() => (
              <OptionList
                searchable={groups.length > 8}
                searchPlaceholder="Find a group…"
                hint="Includes sub-groups. Groups and cameras ADD together — pick both to search the group plus that camera."
                options={(groups as any[]).map((g) => ({ value: g.id, label: g.name }))}
                selected={filters.groups}
                onChange={(v) => set('groups', v)}
                emptyText="No matching group"
              />
            )}
          </FilterDropdown>

          <FilterDropdown
            label="Cameras"
            summary={listSummary(filters.cameras, cameraNames)}
            active={filters.cameras.length > 0}
            disabled={cameras.length === 0}
            disabledReason="No cameras visible to you on this node"
            panelClassName="w-72"
          >
            {() => (
              <OptionList
                searchable
                searchPlaceholder="Find a camera…"
                options={cameras.map((c) => ({ value: c.id, label: c.name }))}
                selected={filters.cameras}
                onChange={(v) => set('cameras', v)}
                emptyText="No matching camera"
              />
            )}
          </FilterDropdown>

          {/*
            How the "which areas?" answer buckets its places. Shown only for that question,
            because it is the only one it changes — the verdict and the journey pin their own
            grouping (camera), for reasons the DSL builder explains.
          */}
          {filters.question === 'where' && (
            <FilterDropdown
              label="Group by"
              summary={PLACE_BY_OPTIONS.find((o) => o.value === filters.placeBy)?.label || 'Area'}
              active
              panelClassName="w-64"
            >
              {(close) => (
                <OptionList
                  mode="single"
                  hint="An AREA is a camera group, so areas nest and their rows overlap. Camera and zone rows do not."
                  options={PLACE_BY_OPTIONS.map<FilterOption>((o) => ({
                    value: o.value,
                    label: o.label,
                    disabledReason: o.hint,
                  }))}
                  selected={[filters.placeBy]}
                  onChange={([v]) => set('placeBy', v as SearchPlaceBy)}
                  close={close}
                />
              )}
            </FilterDropdown>
          )}


          {/* The remaining models — fire, fall, intrusion, PPE, theft, people-counting. Kept as
              its own control so selecting a subject never makes them unreachable. */}
          <FilterDropdown
            label="Other types"
            summary={filters.otherModels.length === 0 ? 'None'
              : filters.otherModels.length === 1
                ? (MODEL_LABEL[filters.otherModels[0]] || titleCase(filters.otherModels[0]))
                : `${filters.otherModels.length} selected`}
            active={filters.otherModels.length > 0}
            panelClassName="w-60"
          >
            {() => (
              <OptionList
                hint={`Added ALONGSIDE ${subjectDef.label.toLowerCase()} events. These models carry no attributes and no subject, so the ${subjectDef.label.toLowerCase()} filters do not apply to them — and they cannot appear in a Journey answer.`}
                options={(schema?.models || Object.keys(MODEL_LABEL))
                  .filter((m) => !SUBJECT_MODELS.has(m))
                  .map<FilterOption>((m) => ({
                    value: m,
                    label: MODEL_LABEL[m] || titleCase(m),
                    group: MODEL_GROUP[m] || 'Other',
                  }))}
                selected={filters.otherModels}
                onChange={(v) => set('otherModels', v)}
                emptyText="Schema not loaded"
              />
            )}
          </FilterDropdown>

          {nActive > 0 && (
            <button
              type="button"
              // Keeps the SUBJECT and the QUESTION: "clear all" means clear the filters, not
              // change what is being asked. Being bounced back to Person or to the plain list
              // mid-investigation would be a surprise.
              onClick={() => {
                setPage(0);
                setParsed(null);
                setFilters({ ...EMPTY_FILTERS, subject: filters.subject, question: filters.question });
              }}
              className="ml-auto text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Clear all ({nActive})
            </button>
          )}
        </div>

        {/* Row 2 — the SUBJECT, and the filters that belong to it */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="w-[4.5rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Looking for
          </span>

          {/* The subject switch. A segmented control rather than a dropdown because it decides
              which questions are askable — a garment colour is meaningless on an ANPR event and
              a plate is meaningless on a person. */}
          <div className="flex overflow-hidden rounded-md border border-border" role="radiogroup" aria-label="Subject">
            {subjectKeys.map((key) => {
              const def = resolveSubjectDef(subjects, key);
              const isOn = filters.subject === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={isOn}
                  onClick={() => {
                    // Attributes are NOT cleared on a subject switch: the two sets are disjoint,
                    // so a person colour is simply not sent with a vehicle query and is still
                    // there when the operator switches back. `subjectModels` IS cleared, because
                    // a model key from one subject is not a valid choice for the other.
                    setPage(0);
                    setParsed(null);
                    setFilters((prev) => ({ ...prev, subject: key, subjectModels: [] }));
                  }}
                  title={`Searches ${def.defaultModels.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')} by default`}
                  className={[
                    'px-3 py-1.5 text-xs font-medium transition-colors',
                    isOn ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  {def.label}
                </button>
              );
            })}
          </div>

          {/*
            "Detected by" — which of the subject's models to search.
            Only offered when the subject HAS a choice: on Vehicle it would be a dropdown with one
            option, which is a control that teaches nothing and can only be clicked wrongly.
          */}
          {subjectDef.models.length > 1 && (
            <FilterDropdown
              label="Detected by"
              summary={activeModels.length === subjectDef.models.length
                ? 'All models'
                : activeModels.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')}
              active={activeModels.length !== subjectDef.defaultModels.length}
              panelClassName="w-72"
            >
              {() => (
                <OptionList
                  hint="Person events carry the clothing attributes; face events carry the matched identity. Selecting both can show one person twice in a list — the When, Which-areas, Journey and Compliance answers count distinct people and are not affected."
                  options={subjectDef.models.map<FilterOption>((m) => ({
                    value: m,
                    label: MODEL_LABEL[m] || titleCase(m),
                    count: undefined,
                    disabledReason: subjectDef.defaultModels.includes(m) ? 'Searched by default' : undefined,
                  }))}
                  selected={activeModels}
                  onChange={(v) => set('subjectModels', v)}
                  emptyText="Schema not loaded"
                />
              )}
            </FilterDropdown>
          )}

          {attributeFields.map((field) => {
            const available = facets[field];
            // Facets loaded and empty → nothing of this kind has been recorded in the current
            // scope. Reported as a NOTICE inside the panel, not as a disabled control.
            const notCollected = Array.isArray(available) && available.length === 0;

            // ALWAYS the full vocabulary from the schema — every value the system can record,
            // whether it has been seen yet or not. Facets supply the COUNTS, not the options.
            const values = schema?.attributes?.[field]
              || (available || []).map((f) => f.value);
            const counts = Object.fromEntries((available || []).map((f) => [f.value, f.events]));
            const chosen = filters.attributes[field] || [];
            const label = FIELD_LABEL[field] || titleCase(field);
            // Under a compliance question this field IS the measurement, so filtering on it too
            // would make the ratio 100%. Said on the control rather than discovered in the answer.
            const isSplitField = filters.question === 'compliance' && filters.splitBy?.field === field;

            return (
              <FilterDropdown
                key={field}
                label={label}
                summary={summariseValues(chosen)}
                active={chosen.length > 0}
                // Up to three swatches on the closed button, so several chosen colours are
                // visible as colours rather than collapsed into a bare "3 selected".
                adornment={COLOR_FIELDS.has(field) && chosen.length > 0 ? (
                  <span className="flex -space-x-0.5">
                    {chosen.slice(0, 3).map((v) => (
                      <Swatch key={v} value={v} className="ring-card" />
                    ))}
                  </span>
                ) : undefined}
                panelClassName={COLOR_FIELDS.has(field) ? 'w-56' : 'w-52'}
              >
                {() => (COLOR_FIELDS.has(field) ? (
                  <SwatchGrid
                    values={values}
                    selected={chosen}
                    counts={counts}
                    onChange={(v) => setAttribute(field, v)}
                    notice={isSplitField
                      ? `This is what the compliance answer is measuring, so a filter here is ignored — every match would have a ${titleCase(filters.splitBy!.value).toLowerCase()}.`
                      : notCollected ? NOT_COLLECTED_NOTICE : undefined}
                  />
                ) : (
                  <OptionList
                    hint={isSplitField
                      ? `This is what the compliance answer is measuring, so a filter here is ignored — every match would have a ${titleCase(filters.splitBy!.value).toLowerCase()}.`
                      : notCollected ? NOT_COLLECTED_NOTICE : undefined}
                    options={values.map<FilterOption>((v) => ({
                      value: v,
                      label: titleCase(v),
                      // 0 rather than undefined, so the list dims what has no data in scope
                      // while still letting it be picked.
                      count: counts[v] ?? 0,
                    }))}
                    selected={chosen}
                    onChange={(v) => setAttribute(field, v)}
                    emptyText="Vocabulary not loaded"
                  />
                ))}
              </FilterDropdown>
            );
          })}

          {/*
            What the compliance answer measures. Sits with the other appearance controls because
            it reads like one — the difference is that this value DIVIDES the matching people
            rather than narrowing them, which the panel's own hint spells out.
          */}
          {filters.question === 'compliance' && (
            <FilterDropdown
              label="Had a"
              summary={filters.splitBy ? titleCase(filters.splitBy.value) : 'Pick one'}
              active={!!filters.splitBy}
              panelClassName="w-64"
            >
              {(close) => (
                <OptionList
                  mode="single"
                  searchable={splitOptions.length > 12}
                  searchPlaceholder="Find a value…"
                  hint="Divides the matching PEOPLE into those who had it and those who did not. One sighting showing it is enough."
                  options={splitOptions}
                  selected={filters.splitBy ? [`${filters.splitBy.field}:${filters.splitBy.value}`] : []}
                  onChange={([v]) => {
                    const [field, ...rest] = v.split(':');
                    set('splitBy', { field, value: rest.join(':') });
                  }}
                  close={close}
                  emptyText="Vocabulary not loaded"
                />
              )}
            </FilterDropdown>
          )}

          {/* The identity filter, which is subject-specific: a person is known or not, a vehicle
              is identified by its plate. Same slot, because it answers the same question. */}
          {subjectDef.textFilter === 'plate' ? (
            <FilterDropdown
              label="Plate"
              summary={filters.plate.trim() || 'Any'}
              active={!!filters.plate.trim()}
              panelClassName="w-56"
              align="right"
            >
              {() => (
                <div className="p-1">
                  <input
                    type="text"
                    autoFocus
                    value={filters.plate}
                    onChange={(e) => set('plate', e.target.value)}
                    placeholder="MH12AB1234 or just MH12"
                    className="w-full rounded border border-border bg-background px-2 py-1 text-xs uppercase text-foreground placeholder:normal-case placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  />
                  {/* Stated because it changes what the operator types: a partial read is a
                      first-class input here, and spacing does not matter on either side. */}
                  <p className="mt-1.5 px-0.5 text-[10px] leading-snug text-muted-foreground">
                    Matches from the start, so <strong className="text-foreground">MH12</strong> finds the whole
                    series. Spaces and dashes are ignored. Needs at least 3 characters.
                  </p>
                  {filters.plate.trim() && (
                    <button
                      type="button"
                      onClick={() => set('plate', '')}
                      className="mt-1 text-[10px] font-medium uppercase tracking-wide text-primary hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </FilterDropdown>
          ) : (
            <FilterDropdown
              label="Identity"
              summary={filters.known === null ? 'Any' : filters.known ? 'Enrolled' : 'Unknown'}
              active={filters.known !== null}
              panelClassName="w-56"
              align="right"
            >
              {(close) => (
                <OptionList
                  mode="single"
                  // Spelled out because "known" is a weaker claim than it sounds: it means the
                  // face matched a registered person, which is the nearest thing to a staff
                  // roster the system holds and is not the roster itself.
                  hint="“Enrolled” means the face matched a registered person. It is the closest the system gets to a staff list."
                  options={[
                    { value: 'any', label: 'Anyone' },
                    { value: 'known', label: 'Enrolled people only' },
                    { value: 'unknown', label: 'Unidentified only' },
                  ]}
                  selected={[filters.known === null ? 'any' : filters.known ? 'known' : 'unknown']}
                  onChange={([v]) => set('known', v === 'any' ? null : v === 'known')}
                  close={close}
                />
              )}
            </FilterDropdown>
          )}
        </div>

        {/* Row 3 — what IS selected, in one line, individually removable */}
        {nActive > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
            {(filters.time.preset !== 'today' || filters.time.from) && (
              <ActiveChip label={timeSummary} onRemove={() => set('time', { preset: 'today' })} />
            )}
            {/* The dragged range appears here like every other narrowing, so it can be undone
                from the filter bar and is never a hidden reason the results look short. */}
            {filters.range && (
              <ActiveChip label={`Range: ${rangeSummary}`} onRemove={() => setRange(null)} />
            )}
            {filters.groups.map((id) => (
              <ActiveChip
                key={id}
                label={`Group: ${groupNames.get(id) || id.slice(0, 8)}`}
                onRemove={() => set('groups', filters.groups.filter((g) => g !== id))}
              />
            ))}
            {filters.cameras.map((id) => (
              <ActiveChip
                key={id}
                label={`Camera: ${cameraNames.get(id) || id.slice(0, 8)}`}
                onRemove={() => set('cameras', filters.cameras.filter((c) => c !== id))}
              />
            ))}
            {/* Only shown when it differs from the default, so the chip row reports a CHOICE
                rather than restating the page's own starting state. */}
            {activeModels.length !== subjectDef.defaultModels.length && (
              <ActiveChip
                label={`Detected by: ${activeModels.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')}`}
                onRemove={() => set('subjectModels', [])}
              />
            )}
            {filters.otherModels.map((m) => (
              <ActiveChip
                key={m}
                label={MODEL_LABEL[m] || titleCase(m)}
                onRemove={() => set('otherModels', filters.otherModels.filter((x) => x !== m))}
              />
            ))}
            {/* Only the ACTIVE subject's attributes: a colour still selected on the Person tab is
                not part of a Vehicle query, so showing it as active would misdescribe the search. */}
            {subjectDef.fields.flatMap((field) => (filters.attributes[field] || []).map((v) => (
              <ActiveChip
                key={`${field}:${v}`}
                adornment={<Swatch value={v} />}
                label={`${FIELD_LABEL[field] || titleCase(field)}: ${titleCase(v)}`}
                onRemove={() => setAttribute(field, (filters.attributes[field] || []).filter((x) => x !== v))}
              />
            )))}
            {subjectDef.textFilter === 'person' && filters.known !== null && (
              <ActiveChip
                label={filters.known ? 'Enrolled people' : 'Unidentified only'}
                onRemove={() => set('known', null)}
              />
            )}
            {subjectDef.textFilter === 'plate' && filters.plate.trim() && (
              <ActiveChip
                label={`Plate: ${filters.plate.trim().toUpperCase()}`}
                onRemove={() => set('plate', '')}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Status. The figures now live in each answer, so this is only what the answer cannot
             say about itself: whether it is loading, whether it failed, and how long it took. ── */}
      <div className="flex items-center gap-4 border-b border-border px-4 py-2 text-sm">
        {loading ? (
          <span className="text-muted-foreground">Searching…</span>
        ) : error ? (
          <span className="flex items-center gap-1.5 text-red-500">
            <ExclamationTriangleIcon className="h-4 w-4" />
            {error}
          </span>
        ) : (
          <>
            {/* The list is the one answer with no headline of its own. */}
            {filters.question === 'list' && (
              <>
                <span className="text-foreground">
                  <strong>{total}</strong> {total === 1 ? 'detection' : 'detections'}
                </span>
                {typeof distinct === 'number' && (
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">{distinct}</strong>{' '}
                    {distinct === 1 ? 'distinct person' : 'distinct people'}
                  </span>
                )}
                {/* Said out loud, not left blank. The server skips the distinct-subject count on
                    a wide unfiltered window because it costs seconds there — and a missing number
                    that looks like an absent one would be read as zero. */}
                {result?.distinct_omitted && (
                  <span
                    className="text-muted-foreground"
                    title="Counting distinct people across this many events takes seconds, so it was skipped. Narrow the window or add a filter and the count returns."
                  >
                    distinct people <strong className="text-foreground">—</strong>{' '}
                    <span className="text-xs">(too many events to count; narrow the window)</span>
                  </span>
                )}
                {typeof result?.camera_scope === 'number' && (
                  <span className="text-xs text-muted-foreground">
                    across {result.camera_scope} {result.camera_scope === 1 ? 'camera' : 'cameras'}
                  </span>
                )}
              </>
            )}
            {filters.question !== 'list' && (
              <span className="text-xs text-muted-foreground">{questionSpec.help}</span>
            )}
            {typeof result?.took_ms === 'number' && (
              <span className="ml-auto text-xs text-muted-foreground">{result.took_ms} ms</span>
            )}
          </>
        )}
      </div>

      {/* ── The answer ── */}
      <div className="min-h-0 flex-1 overflow-auto">
        {loading && !result ? (
          <div className="flex h-full items-center justify-center"><LoadingSpinner /></div>
        ) : (
          renderAnswer()
        )}
      </div>

      {/* ── The event timeline, docked at the bottom.
             Below the answer rather than above it, because it is read AFTER the answer: "the
             results are thin — where in the day were they". Outside the scrolling region, so it
             stays reachable while the operator is deep in a page of cards — a range control you
             have to scroll back up to is a range control nobody uses. ── */}
      <EventTimelineStrip
        result={timeline}
        loading={timelineLoading}
        range={filters.range}
        onRangeChange={setRange}
        markers={markers}
        // The same map the cards above use, so a pin names its camera identically.
        cameraName={(id) => cameraNames.get(id)}
      />

      {/* ── The open record.
             A modal rather than a route, because a search is a position the operator is working
             through: navigating away throws the query, the answer and the scroll position away to
             look at one hit. Same shell and same body the report tables use, so a record found by
             search and a record found by report are the same record. ── */}
      {eventDetail && (
        <Modal
          open={!!detailRow}
          onClose={() => setDetailIndex(null)}
          size="full"
          ariaLabel="Event details"
          title={detailRow && detailEvent && eventDetail.title
            ? eventDetail.title(detailEvent, detailRow)
            : detailRow
              ? `${MODEL_LABEL[detailRow.model_key] || titleCase(detailRow.model_key || '')} event`
              : undefined}
          subtitle={detailRow && [
            cameraNames.get(detailRow.camera_id) || detailRow.camera_id,
            detailRow.event_timestamp ? new Date(detailRow.event_timestamp).toLocaleString() : null,
          ].filter(Boolean).join(' · ')}
          headerActions={detailRow && (
            <>
              {/* Steps through the hits on screen without closing — ← / → are bound by the pager
                  itself. This is the whole reason the modal beats a page for a search result. */}
              <RecordPager
                index={detailIndex ?? 0}
                count={detailRows.length}
                onIndexChange={setDetailIndex}
                noun="results"
              />
              {onOpenEvent && (
                <button
                  type="button"
                  onClick={() => onOpenEvent(detailRow)}
                  title="Open the full event page"
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
                </button>
              )}
            </>
          )}
        >
          {detailLoading ? (
            <div className="p-6"><div className="h-64 animate-pulse rounded-lg bg-muted" /></div>
          ) : detailError ? (
            <div className="py-16 text-center text-sm text-muted-foreground">{detailError}</div>
          ) : detailEvent && detailRow ? (
            eventDetail.render(detailEvent, detailRow, { cameraNames: cameraNameMap })
          ) : null}
        </Modal>
      )}

      {/* ── Docked playback. Overlays the grid rather than replacing it, so an operator can
             play one hit, dismiss it and try the next without losing the query or the scroll
             position. Keyed on the event id so switching cards remounts the video rather than
             re-pointing a playing element at a different source. ── */}
      {playbackRow && playback && (() => {
        const win = playbackWindow(playbackRow);
        if (!win) return null;
        const camera = cameraNames.get(playbackRow.camera_id) || playbackRow.camera_id.slice(0, 8);
        const when = new Date(playbackRow.event_timestamp).toLocaleString();
        const label = MODEL_LABEL[playbackRow.model_key] || titleCase(playbackRow.model_key || '');
        return (
          <SearchPlaybackPanel
            key={playbackRow.event_id}
            title={camera}
            subtitle={`${label} · ${when}`}
            src={search.playbackUrl(playbackRow.camera_id, win.from, win.to)}
            leadSeconds={PLAYBACK_LEAD_SEC}
            tunnelled={playbackTunnelled}
            // Sourced from the adapter, which is where each host's auth already lives. Optional:
            // a host whose playback URL carries its own token needs no header.
            getAuthToken={search.playbackAuthToken ? () => search.playbackAuthToken!() : undefined}
            onOpenFull={onOpenRecordings
              ? () => onOpenRecordings(playbackRow, win.from, win.to)
              : undefined}
            onClose={() => setPlaybackRow(null)}
          />
        );
      })()}

      {/* ── Paging. Only the two answers that HAVE pages: a place roll-up and a timeline are
             bounded by the estate and the window, and offering Next on them would offer a page
             that is always empty. Deliberately prev/next only — a numbered pager over a result
             set that changes as events arrive invites the reader to trust a page number that
             will not mean the same thing a minute later. ── */}
      {questionSpec.paged && (page > 0 || rows.length === limit) && (
        <div className="flex items-center justify-center gap-3 border-t border-border px-4 py-2 text-xs">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-muted-foreground">
            {filters.question === 'journey'
              ? `Trails ${page * limit + 1}–${page * limit + rows.length}`
              : `${page * limit + 1}–${page * limit + rows.length} of ${total}`}
          </span>
          <button
            type="button"
            disabled={rows.length < limit}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
